/* eslint-disable @next/next/no-img-element */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { cn, formatTime } from "@/lib/utils";
import { usePayment, useUser, useUserVip, useGlobalConfig } from "@/stores/appStore";
import { TabsContent } from "@/components/ui/tabs"
import { Order } from "@/stores/slices/paymentSlice";
import { MemberLabel, memberLabelClassName } from "@/components/fun-ui/MemberLabel";
import { InfoIcon, LogoAlipayIcon, LogoWechatIcon } from "@/components/fun-ui/icons";
import { Tip } from "@/components/fun-ui/Tip";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/fun-ui/Dialog";
import { PaymentTabs } from "./PaymentTabs";
import { PaymentDialogUpgrade } from "./PaymentDialogUpgrade";
import { PaymentDialogRecharge } from "./PaymentDialogRecharge";
import { useWebSocketMessage } from "@/context/WebSocketContext";
import Link from "next/link";
import Loading from "../Loading";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "next/navigation";
import { PaymentUpgradeSuccessAlert } from "./PaymentUpgradeSuccessAlert";
import dayjs from "dayjs";
import { useRenewalSonnerOpen } from "@/stores/localStore";
import { Suspense } from "react";
import { PaymentType } from "@/stores/slices/commonSlice";
import toast from "react-hot-toast";

type PaymentDialogProps = {}

function PaymentDialogContent({ }: PaymentDialogProps) {
  const searchParams = useSearchParams();
  const debug = !!searchParams.get('debug');
  const [scale, setScale] = useState(1);
  const [orders, setOrders] = useState<Record<string, Order>>({});
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [upgradePaymentOption, setUpgradePaymentOption] = useState({ renewalDays: 0, paymentPrice: 0 });
  const [rechargePaymentOption, setRechargePaymentOption] = useState({ paymentPrice: 0 });
  const [upgradeActivedIndex, setUpgradeActivedIndex] = useState<number>(0);
  const [rechargeActivedIndex, setRechargeActivedIndex] = useState<number>(0);

  // Refs for storing mutable values
  const ordersRef = useRef<Record<string, Order>>({});
  const urlsRef = useRef<Record<string, string>>({});
  const isMountedRef = useRef(true);
  const isPaidRef = useRef(false);
  const paymentTypeRef = useRef<PaymentType>('upgrade');

  // Store hooks
  const {
    createOrder,
    getPaymentUrl,
    paymentOpen,
    setPaymentOpen,
    paymentType,
    getPaymentPrice,
    setPaymentType,
    paymentCallbak,
    clearPaymentCallback,
    paymentSuccessOpenWithTitle,
    setPaymentSuccessOpenWithTitle,
  } = usePayment();

  const { refreshUserInfo } = useUser();
  const { vip: isVip, vipExtra, remainingDays = 0 } = useUserVip();
  const { globalConfig } = useGlobalConfig();

  // 调用 fetch 获取配置，fetch 内部已有保护逻辑（已存在或正在加载会直接返回）
  useEffect(() => {
    globalConfig?.fetch?.();
  }, [globalConfig]);

  // 从远程配置获取 paymentTabs，默认为空数组
  const paymentTabs = useMemo(() => {
    return globalConfig?.value?.paymentTabs || [];
  }, [globalConfig]);

  const paymentPrice = getPaymentPrice();
  const renewalDays = upgradePaymentOption.renewalDays;

  const isOver2Years = useMemo(() => {
    return paymentType === 'upgrade' && (remainingDays + renewalDays) > (25 * 31);
  }, [paymentType, remainingDays, renewalDays]);

  // Generate current key for caching orders and URLs
  const currentKey = useMemo(() => (
    `${paymentType}_${paymentPrice}`
  ), [paymentType, paymentPrice]);

  // ==================== 生命周期管理 ====================
  useEffect(() => {
    isMountedRef.current = true;

    // 计算缩放比例
    const calculateScale = () => {
      const scale = Math.min((Number(window.innerHeight) || 818) / 818, 1);
      setScale(scale);
    };

    calculateScale();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ==================== 用户信息刷新 ====================
  useEffect(() => {
    if (paymentOpen) {
      refreshUserInfo();
    }
  }, [paymentOpen, refreshUserInfo]);

  // ==================== 同步 paymentType 到 ref ====================
  useEffect(() => {
    paymentTypeRef.current = paymentType;
  }, [paymentType]);

  // ==================== WebSocket 消息处理 ====================
  const handlePaymentMessage = useCallback(async (data: any) => {
    const handlePaymentSuccess = async () => {
      await refreshUserInfo();
      isPaidRef.current = true;
      // 使用 ref 获取最新的 paymentType，避免闭包问题
      const currentPaymentType = paymentTypeRef.current;
      if (currentPaymentType === 'recharge') {
        // 充值没有反馈窗口
        // 显示成功提示
        toast.success('飞豆购买成功');
        // 回调，状态返回
        if (paymentCallbak) {
          paymentCallbak({
            type: 'recharge',
            status: 'success',
          });
          clearPaymentCallback();
        }
      } else {
        // 升级会员反馈窗口
        // 回调在窗口关闭后处理
        setPaymentSuccessOpenWithTitle(true);
      }
      setPaymentOpen(false);
    };

    const { event, orderNo, order_no } = data;
    if (event === 'paid') {
      // 付款后会关闭窗口
      handlePaymentSuccess();
    } else if (event === 'scanned') {
      const currentOrderNo = ordersRef.current[currentKey]?.orderNo;
      const scannedOrderNo = orderNo || order_no;
      // 若服务端回传订单号，则只刷新当前订单对应二维码，避免误触发
      if (scannedOrderNo && currentOrderNo && scannedOrderNo !== currentOrderNo) {
        return;
      }
      // 二维码被扫码后作废，清空并触发重新创建订单获取新 payUrl
      setOrders(prev => {
        const newOrders = { ...prev };
        delete newOrders[currentKey];
        return newOrders;
      });
      setUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[currentKey];
        return newUrls;
      });
      setRefreshCounter(prev => prev + 1);
    } else {
      console.log('WebSocket message:', data);
    }
  }, [paymentCallbak, clearPaymentCallback, currentKey, refreshUserInfo, setPaymentOpen, setPaymentSuccessOpenWithTitle]);

  useWebSocketMessage(handlePaymentMessage, 'payment');

  // ==================== Refs 同步 ====================
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    urlsRef.current = urls;
  }, [urls]);

  // ==================== 订单创建逻辑 ====================
  useEffect(() => {
    // 如果刷新计数器不为0，则跳过创建新订单
    if (refreshCounter) { }
    // 跳过条件检查
    if (isVip !== 0 && isVip !== 1) return; // VIP状态未加载完成
    if (ordersRef.current[currentKey] || !paymentPrice) return; // 已有订单或无价格
    if (paymentType === 'upgrade') {
      // 价格未同步更新
      if (upgradePaymentOption.paymentPrice !== paymentPrice) {
        return;
      }
      // 如果续费后总天数超过 24 个月，则不创建订单
      if (isOver2Years) {
        console.log('超出2年限制，不创建订单');
        return;
      }
    }

    let price = debug ? 0.01 : paymentPrice; // 调试模式使用0.01元

    const createNewOrder = async () => {
      try {
        const order = await createOrder(price);
        if (order && isMountedRef.current) {
          setOrders(prev => ({
            ...prev,
            [currentKey]: order,
          }));
          if (order.payUrl) {
            setUrls(prev => ({
              ...prev,
              [currentKey]: order.payUrl,
            }));
          } else if (order.orderNo) {
            // 兼容后端灰度：若未返回 payUrl，回退旧 prepay 获取逻辑
            const fallbackUrl = await getPaymentUrl(order.orderNo);
            if (fallbackUrl && isMountedRef.current) {
              setUrls(prev => ({
                ...prev,
                [currentKey]: fallbackUrl,
              }));
            }
          }
        }
      } catch (error) {
        console.error('创建订单失败:', error);
      }
    };
    createNewOrder();
  }, [
    currentKey,
    createOrder,
    getPaymentUrl,
    debug,
    isOver2Years,
    isVip,
    paymentType,
    paymentPrice,
    upgradePaymentOption,
    // 触发创建新订单
    refreshCounter
  ]);

  const {
    resetOpenTime,
  } = useRenewalSonnerOpen();

  // 处理升级支付选项变化的回调
  const handleUpgradePaymentOptionChange = useCallback((option: { renewalDays: number, paymentPrice: number }) => {
    setUpgradePaymentOption(option);
  }, [setUpgradePaymentOption]);

  const closeAndReset = useCallback(() => {
    // 关闭窗口时重置状态
    isPaidRef.current = false;
    setOrders({});
    setUrls({});
    setRefreshCounter(0);
    setUpgradePaymentOption({ renewalDays: 0, paymentPrice: 0 });
    setRechargePaymentOption({ paymentPrice: 0 });
    setUpgradeActivedIndex(0);
    setRechargeActivedIndex(0);
  }, []);

  // ==================== 对话框关闭清理 ====================
  // 只在组件卸载时调用 closeAndReset，避免依赖项变化导致频繁执行
  useEffect(() => {
    return () => {
      if (isMountedRef.current) {
        // 使用 ref 来避免依赖项变化
        isPaidRef.current = false;
        setOrders({});
        setUrls({});
        setRefreshCounter(0);
        setUpgradePaymentOption({ renewalDays: 0, paymentPrice: 0 });
        setRechargePaymentOption({ paymentPrice: 0 });
        setUpgradeActivedIndex(0);
        setRechargeActivedIndex(0);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组，只在组件卸载时执行

  // ==================== 对话框状态处理 ====================
  const handleDialogOpenChange = useCallback((open: boolean) => {
    // 避免同值重复设置导致的渲染递归
    if (!!paymentOpen === open) return;
    setPaymentOpen(open);
    // 没有支付就关闭
    if (!open && !isPaidRef.current) {
      // 回调，状态返回
      if (paymentCallbak) {
        paymentCallbak({
          type: paymentType,
          status: 'cancel',
        });
        clearPaymentCallback();
      }
      closeAndReset();
    }
  }, [paymentCallbak, clearPaymentCallback, paymentOpen, paymentType, closeAndReset, setPaymentOpen]);


  // ==================== 渲染逻辑 ====================
  return (
    <>
      <Dialog open={!!paymentOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          showCloseButton
          closeClassName="top-4 right-4"
          className={cn(
            "p-0 m-0",
            "border-none outline-none shadow-lg bg-background sm:rounded-3xl bg-gradient-to-b from-[#ecf1ff] overflow-hidden",
          )}
          style={{
            width: `${1154 * scale}px`,
            height: `${818 * scale}px`,
            maxWidth: "calc(100% - 2rem)",
            maxHeight: "calc(100vh - 2rem)",
            display: "flex",
            padding: 0,
            margin: 0,
            gap: 0,
            paddingTop: `${32 * scale}px`,
          }}
          tabIndex={-1}
        // onPointerDownOutside={(evt) => evt.preventDefault()}
        // onOpenAutoFocus={(evt) => evt.preventDefault()}
        >
          <div
            className={cn(
              "overflow-hidden w-full h-full",
            )}
          >
            {/* 背景图片渲染 */}
            {paymentTabs.map(({ value }: { value: PaymentType }) => (
              <div
                key={value}
                hidden={paymentType !== value}
                className={value === 'upgrade'
                  ? "absolute -right-[100px] -bottom-[120px] pointer-events-none"
                  : "absolute right-0 top-0 pointer-events-none"
                }
                style={value === 'upgrade' ? {
                  right: `${-100 * scale}px`,
                  bottom: `${-120 * scale}px`,
                } : {}}
              >
                <Image
                  priority
                  unoptimized={true}
                  src={value === 'upgrade'
                    ? "https://oss.findinai.cn/storage/20250905/7acdc042a9f9aac4371b1a9aecbe7e18.png"
                    : "https://oss.findinai.cn/storage/20260111/012dac8f62b7da7ed1bc0e0238f7ab1a.png"
                  }
                  width={value === 'upgrade' ? 585 : 434}
                  height={value === 'upgrade' ? 547 : 764}
                  alt=""
                  style={{
                    width: `${value === 'upgrade' ? 585 : 434 * scale}px`,
                    height: `${value === 'upgrade' ? 547 : 764 * scale}px`,
                  }}
                />
              </div>
            ))}

            <PaymentTabs>
              {/* 升级选项卡内容 */}
              {paymentTabs.some((tab: { value: PaymentType }) => tab.value === 'upgrade') && (
                <TabsContent
                  key="upgrade"
                  value="upgrade"
                  style={{ marginTop: `${16 * scale}px` }}
                >
                  <PaymentDialogUpgrade
                    scale={scale}
                    activedIndex={upgradeActivedIndex}
                    onActivedIndexChange={setUpgradeActivedIndex}
                    onPaymentOptionChange={handleUpgradePaymentOptionChange}
                  />
                </TabsContent>
              )}
              {/* 充值选项卡内容 */}
              {paymentTabs.some((tab: { value: PaymentType }) => tab.value === 'recharge') && (
                <TabsContent
                  key="recharge"
                  value="recharge"
                  style={{ marginTop: `${16 * scale}px` }}
                >
                  <PaymentDialogRecharge
                    scale={scale}
                    activedIndex={rechargeActivedIndex}
                    onActivedIndexChange={setRechargeActivedIndex}
                    onPaymentOptionChange={setRechargePaymentOption}
                  />
                </TabsContent>
              )}

              {/* 支付二维码区域 */}
              {(isOver2Years) ? (
                <div
                  className="flex flex-col"
                  style={{
                    gap: `${16 * scale}px`,
                    marginTop: `${36 * scale}px`,
                    marginLeft: `${60 * scale}px`,
                  }}
                >
                  <div style={{
                    fontSize: `${48 * scale}px`,
                    lineHeight: `${56 * scale}px`,
                  }} className=" flex flex-row items-center">
                    <MemberLabel className="font-semibold">超出会员有效期上限</MemberLabel>
                  </div>
                  <div className="text-muted-foreground" style={{
                    fontSize: `${14 * scale}px`,
                    lineHeight: `${20 * scale}px`,
                  }}>
                    <span className="font-medium">当前 Plus 会员有效期至：{formatTime(vipExtra?.expireTime!, 'YYYY年MM月DD日')}</span><br />
                    所选会员方案续费后将超出 2 年有效期上限，请更换会员方案或使用一段时间后再续费。<br />
                    感谢你对 Findin 的喜爱和支持！
                  </div>
                </div>
              ) : (
                <div
                  className="flex justify-between"
                  style={{
                    marginTop: `${24 * scale}px`,
                    marginLeft: `${60 * scale}px`,
                  }}
                >
                  <div className="flex" style={{ gap: `${24 * scale}px` }}>
                    {/* 二维码区域 */}
                    <Link
                      className="cursor-default relative block"
                      style={{
                        width: `${130 * scale}px`,
                        height: `${130 * scale}px`,
                      }}
                      target={debug ? "_blank" : ''}
                      href={(debug && urls[currentKey]) ? urls[currentKey] : ''}
                    >
                      {!urls[currentKey] ? (
                        <Loading mask size="lg" />
                      ) : (
                        <QRCodeSVG
                          data-value={urls[currentKey]}
                          value={urls[currentKey]}
                          size={130 * scale}
                        />
                      )}
                    </Link>

                    {/* 价格信息区域 */}
                    <div
                      className="flex flex-col justify-end"
                      style={{ gap: `${10 * scale}px` }}
                    >
                      <div className={cn(
                        "font-semibold align-baseline",
                        paymentType === 'upgrade' ? memberLabelClassName : "text-primary",
                      )}>
                        <span style={{
                          fontSize: `${20 * scale}px`,
                          lineHeight: `${28 * scale}px`
                        }}>￥</span>
                        <span style={{
                          fontSize: `${36 * scale}px`,
                          lineHeight: `${40 * scale}px`
                        }}>{paymentPrice}</span>
                      </div>
                      {
                        isVip === 1 && renewalDays > 0 && paymentType === 'upgrade' ? (
                          <div style={{
                            fontSize: `${14 * scale}px`,
                            lineHeight: `${20 * scale}px`
                          }}>
                            <MemberLabel className="font-medium">当前 Plus 会员有效期至：{formatTime(vipExtra?.expireTime!, 'YYYY-MM-DD')}，续费后将延至：{formatTime(dayjs(vipExtra?.expireTime!).add(renewalDays, 'day').toISOString(), 'YYYY-MM-DD')}</MemberLabel>
                          </div>
                        ) : null
                      }
                      <div className="flex flex-row items-center" style={{
                        fontSize: `${14 * scale}px`,
                        lineHeight: `${20 * scale}px`
                      }}>
                        <span>使用</span>
                        <LogoAlipayIcon style={{
                          marginLeft: `${4 * scale}px`,
                          marginRight: `${4 * scale}px`,
                        }} />
                        <span>支付宝App</span>
                        <LogoWechatIcon style={{
                          marginLeft: `${4 * scale}px`,
                          marginRight: `${4 * scale}px`,
                        }} />
                        <span>微信App</span>
                        <span style={{
                          marginLeft: `${4 * scale}px`,
                        }}>扫码支付</span>
                      </div>

                      <div style={{
                        fontSize: `${14 * scale}px`,
                        lineHeight: `${20 * scale}px`
                      }}>
                        <span>购买即表示接受</span>
                        <Link
                          className="text-primary hover:underline"
                          target="_blank"
                          href="/docs/payment-terms-of-service"
                        >《付款服务条款》</Link>
                        <span>支持开具电子普通发票</span>
                        <Tip
                          label="Findin 飞引支持开具电子普通发票，发票明细支持以下选择：文献检索费/信息服务费/数据服务费/技术服务费。支付成功后，你可以在订单记录-发票管理中进行申请。"
                          className="ml-1 cursor-help"
                          contentClassName="max-w-[320px]"
                        >
                          <InfoIcon size={14} />
                        </Tip>
                      </div>
                    </div>
                  </div>
                  <Link
                    target="_blank"
                    href={`/pricing?type=${paymentType}`}
                    className="self-end text-sm text-primary hover:underline"
                    style={{ marginRight: `${40 * scale}px` }}
                  >
                    了解更多
                  </Link>
                </div>
              )}
            </PaymentTabs>
          </div>
        </DialogContent>
      </Dialog>

      <PaymentUpgradeSuccessAlert
        title={typeof paymentSuccessOpenWithTitle === 'string' ? paymentSuccessOpenWithTitle : ''}
        open={!!paymentSuccessOpenWithTitle && paymentType === 'upgrade'}
        setOpen={(open) => {
          if (!open) {
            // 回调，状态返回
            if (paymentCallbak) {
              paymentCallbak({
                type: 'upgrade',
                status: 'success',
              });
              clearPaymentCallback();
            }
            resetOpenTime();
            closeAndReset();
          }
          setPaymentSuccessOpenWithTitle(open);
        }}
      />
    </>
  );
}

export function PaymentDialog({ }: PaymentDialogProps) {
  return (
    <Suspense fallback={null}>
      <PaymentDialogContent />
    </Suspense>
  );
}